import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Sparkles, Loader2, AlertCircle, Image, X, ArrowLeft, Camera, Upload, Save, Wand2, BookOpen, Tag, ChevronRight, Check, Eye, EyeOff, Lock, Globe } from 'lucide-react';
import ImageCropper from '../components/ImageCropper';
import TemplateCard from '../components/TemplateCard';
import { formatApiError, validateCharacterForm } from '../utils/apiError';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ALL_TAGS = {
  "Type": ["OC", "Anime", "Game", "Movie and TV", "Celebrity", "Text Game Based RPG"],
  "Archetype": ["Billionaire", "Criminal", "Femboy", "Best Friend", "Nerd", "Killer", "Mafia", "Non Human", "Student", "Teacher", "Tomboy", "Vampire", "Werewolf", "YouTuber", "Sugar Mommy/Daddy"],
  "Personality": ["Caring", "Clingy", "Clumsy", "Cold", "Crazy", "Cunning", "Cute", "Innocent", "Dedicated", "Dominant", "Easy Angered", "Ego Defensive", "Energetic", "Elegant", "Flirtatious", "Funny", "Gentle", "Manipulative", "Intelligent", "Jealous", "Loyal", "Submissive", "Mean", "Possessive", "Obsessive", "Sharp Tongued", "Wild", "Sweet", "Yandere", "Tsundere"],
  "Genre": ["Adventure", "BL", "GL", "Comedy", "Crime", "Fantasy", "Forbidden Love", "Enemies to Lovers", "Friends to Lovers", "RPG", "Sci-Fi", "Secret Crush", "Slice of Life"],
};

const MAX_TAGS = 5;

function TagPickerModal({ isOpen, onClose, selectedTags, onTagsChange }) {
  if (!isOpen) return null;

  const toggleTag = (tag) => {
    if (selectedTags.includes(tag)) {
      onTagsChange(selectedTags.filter(t => t !== tag));
    } else if (selectedTags.length < MAX_TAGS) {
      onTagsChange([...selectedTags, tag]);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-lg bg-[#0F1115] border-t border-white/10 rounded-t-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/5">
          <div>
            <h3 className="text-white font-bold font-['Unbounded'] text-base">Select Tags</h3>
            <p className="text-xs text-[#71717A] mt-0.5">{selectedTags.length}/{MAX_TAGS} selected</p>
          </div>
          <button onClick={onClose} className="p-2 text-[#71717A] hover:text-white" data-testid="close-tag-picker">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Selected Tags */}
        {selectedTags.length > 0 && (
          <div className="px-4 pt-3 pb-1 flex flex-wrap gap-1.5">
            {selectedTags.map(tag => (
              <button
                key={tag}
                onClick={() => toggleTag(tag)}
                className="px-2.5 py-1 text-xs rounded-full bg-[#E11D48]/20 text-[#E11D48] border border-[#E11D48]/30 hover:bg-[#E11D48]/30 transition-colors flex items-center gap-1"
                data-testid={`selected-tag-${tag}`}
              >
                {tag} <X className="w-3 h-3" />
              </button>
            ))}
          </div>
        )}

        {/* Tag Groups */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {Object.entries(ALL_TAGS).map(([group, tags]) => (
            <div key={group}>
              <p className="text-xs font-medium text-[#F59E0B] uppercase tracking-wider mb-2">{group}</p>
              <div className="flex flex-wrap gap-1.5">
                {tags.map(tag => {
                  const isSelected = selectedTags.includes(tag);
                  const isDisabled = !isSelected && selectedTags.length >= MAX_TAGS;
                  return (
                    <button
                      key={tag}
                      onClick={() => !isDisabled && toggleTag(tag)}
                      disabled={isDisabled}
                      className={`px-3 py-1.5 text-xs rounded-full border transition-all ${
                        isSelected
                          ? 'bg-[#E11D48]/20 text-[#E11D48] border-[#E11D48]/40'
                          : isDisabled
                          ? 'bg-white/[0.02] text-[#555] border-white/5 opacity-40'
                          : 'bg-white/5 text-[#A1A1AA] border-white/10 hover:bg-white/10 hover:text-white active:scale-95'
                      }`}
                      data-testid={`tag-${tag}`}
                    >
                      {isSelected && <Check className="w-3 h-3 inline mr-1" />}
                      {tag}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Done Button */}
        <div className="p-4 border-t border-white/5">
          <Button
            onClick={onClose}
            className="w-full bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-xl py-3"
            data-testid="done-tags-btn"
          >
            Done ({selectedTags.length} tags)
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function CreateCharacter() {
  const navigate = useNavigate();
  const { editId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isEditMode = !!editId;
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(null);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [pendingCropImage, setPendingCropImage] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    personality: '',
    greeting: '',
    category: 'custom',
    avatar_url: '',
    tags: '',
    is_public: true,
    gender: '',
    backstory: ''
  });

  // Pick up generated avatar from URL params
  useEffect(() => {
    const avatarUrl = searchParams.get('avatar_url');
    const charName = searchParams.get('name');
    const charDesc = searchParams.get('description');
    if (avatarUrl) {
      setFormData(prev => ({
        ...prev,
        avatar_url: avatarUrl,
        name: charName || prev.name,
        description: charDesc || prev.description
      }));
    }
  }, [searchParams]);

  useEffect(() => {
    if (isEditMode) fetchCharacter();
    if (!isEditMode) fetchTemplates();
  }, [editId]);

  // ---- Backend-persisted draft system (explicit Save — no autosave) ----
  const draftId = searchParams.get('draft') || null;
  const [activeDraftId, setActiveDraftId] = useState(draftId);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);

  // Load draft if ?draft=<id> in URL (create mode only)
  useEffect(() => {
    if (isEditMode || draftLoaded || !draftId) { setDraftLoaded(true); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/character-drafts/${draftId}`, { withCredentials: true });
        const f = data.form_data || {};
        setFormData({
          name: f.name || '',
          description: f.description || '',
          personality: f.personality || '',
          greeting: f.greeting || '',
          category: f.category || 'custom',
          avatar_url: f.avatar_url || '',
          tags: '',
          is_public: f.is_public !== false,
          gender: f.gender || '',
          backstory: f.backstory || '',
        });
        if (Array.isArray(f.tags)) setSelectedTags(f.tags);
        setActiveDraftId(data.id);
      } catch (err) {
        // Draft not found / 401 / etc — just continue with empty form
        console.warn('Could not load draft:', err);
      } finally {
        setDraftLoaded(true);
      }
    })();
  }, [draftId, isEditMode]);

  // Manual save — explicit, instant feedback, then navigates home
  const saveDraft = async (navigateHome = false) => {
    if (isEditMode) return;
    // Don't save an empty draft
    const hasContent = formData.name || formData.description || formData.personality || formData.greeting || formData.backstory || selectedTags.length > 0 || formData.avatar_url;
    if (!hasContent) {
      setError('Add at least a name or some details before saving.');
      setTimeout(() => {
        document.querySelector('[data-testid="create-error"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }
    setDraftSaving(true);
    setError('');
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        personality: formData.personality,
        greeting: formData.greeting,
        backstory: formData.backstory,
        category: formData.category,
        gender: formData.gender,
        avatar_url: formData.avatar_url,
        is_public: formData.is_public,
        tags: selectedTags,
      };
      if (activeDraftId) {
        await axios.put(`${API_URL}/api/character-drafts/${activeDraftId}`, payload, { withCredentials: true });
      } else {
        const { data } = await axios.post(`${API_URL}/api/character-drafts`, payload, { withCredentials: true });
        setActiveDraftId(data.id);
      }
      setDraftSaved(true);
      if (navigateHome) {
        // Brief 600ms "Saved!" flash, then home
        setTimeout(() => navigate('/'), 600);
      } else {
        setTimeout(() => setDraftSaved(false), 1800);
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        setError("Your session has expired. Please log back in and try again.");
      } else {
        setError(formatApiError(err));
      }
    } finally {
      setDraftSaving(false);
    }
  };

  const clearDraft = async () => {
    if (!activeDraftId) return;
    try {
      await axios.delete(`${API_URL}/api/character-drafts/${activeDraftId}`, { withCredentials: true });
    } catch { /* already gone, fine */ }
    setActiveDraftId(null);
  };

  const fetchTemplates = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/character-templates`);
      setTemplates(data);
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  const applyTemplate = async (templateId) => {
    setLoadingTemplate(templateId);
    try {
      const { data } = await axios.get(`${API_URL}/api/character-templates/${templateId}`);
      setFormData({
        name: data.name,
        description: data.description,
        personality: data.personality,
        greeting: data.greeting,
        category: data.category,
        avatar_url: '',
        tags: (data.tags || []).join(', '),
        is_public: true,
        gender: ''
      });
      setSelectedTags(data.tags || []);
      setShowTemplates(false);
    } catch (err) {
      console.error('Error applying template:', err);
    } finally {
      setLoadingTemplate(null);
    }
  };

  const fetchCharacter = async () => {
    setFetching(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/characters/${editId}`, { withCredentials: true });
      setFormData({
        name: data.name,
        description: data.description,
        personality: data.personality || '',
        greeting: data.greeting,
        category: data.category,
        avatar_url: data.avatar_url || '',
        tags: (data.tags || []).join(', '),
        is_public: data.is_public,
        gender: data.gender || '',
        backstory: data.backstory || ''
      });
      setSelectedTags(data.tags || []);
    } catch (err) {
      navigate('/profile');
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;

    // GIFs bypass the cropper to preserve animation
    const isGif = file.type === 'image/gif' || file.name.toLowerCase().endsWith('.gif');
    if (isGif) {
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        setError('GIF must be under 10MB');
        if (e.target) e.target.value = '';
        return;
      }
      setUploading(true);
      setError('');
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file, file.name);
        const { data } = await axios.post(`${API_URL}/api/upload`, formDataUpload, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        handleChange('avatar_url', data.url);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to upload GIF');
      } finally {
        setUploading(false);
        if (e.target) e.target.value = '';
      }
      return;
    }

    // Static images go through the cropper
    if (file.size > 5 * 1024 * 1024) { setError('Image must be under 5MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setPendingCropImage(reader.result);
    reader.readAsDataURL(file);
    if (e.target) e.target.value = '';
  };

  const handleCropConfirm = async (blob) => {
    setUploading(true);
    setError('');
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', blob, 'avatar.jpg');
      const { data } = await axios.post(`${API_URL}/api/upload`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      handleChange('avatar_url', data.url);
      setPendingCropImage(null);
    } catch {
      setError('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Client-side validation first — fail loud BEFORE wasting an API round-trip
    const clientErrors = validateCharacterForm({
      name: formData.name,
      description: formData.description,
      personality: formData.personality,
      greeting: formData.greeting,
      selectedTags,
      avatar_url: formData.avatar_url,
      gender: formData.gender,
    });
    if (clientErrors.length > 0) {
      setError(clientErrors.join(' '));
      // Scroll the error banner into view so the user can read it on mobile
      setTimeout(() => {
        document.querySelector('[data-testid="create-error"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        tags: selectedTags,
      };
      delete payload.tags_string;
      
      if (isEditMode) {
        await axios.put(`${API_URL}/api/characters/${editId}`, payload, { withCredentials: true });
        navigate(`/character/${editId}`);
      } else {
        const { data } = await axios.post(`${API_URL}/api/characters`, payload, { withCredentials: true });
        await clearDraft();
        navigate(`/character/${data.id}`);
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        setError("Your session has expired — but don't worry, your work is safely saved in Drafts (Profile → Drafts). Log back in and re-open it from there.");
      } else {
        setError(formatApiError(err));
        setTimeout(() => {
          document.querySelector('[data-testid="create-error"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 50);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-24 lg:pb-12">
      {/* Header with animated Save button */}
      <div className="sticky top-12 lg:top-0 z-40 bg-[#050505] border-b border-white/5 px-4 lg:px-8 py-3 lg:py-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-[#A1A1AA] hover:text-white" data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-bold text-white font-['Unbounded'] flex-1">
            {isEditMode ? 'Edit Character' : 'Create Character'}
          </h1>
          {!isEditMode && (
            <button
              type="button"
              onClick={() => saveDraft(true)}
              disabled={draftSaving}
              className={`relative overflow-hidden group inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider transition-all shadow-lg ${
                draftSaved
                  ? 'bg-emerald-500 text-white shadow-emerald-500/40'
                  : 'bg-gradient-to-r from-[#FCD34D] via-[#F59E0B] to-[#E11D48] text-[#1A0610] shadow-[#F59E0B]/30 hover:scale-105 active:scale-95'
              } disabled:opacity-70 disabled:cursor-wait`}
              data-testid="save-draft-btn"
              aria-label="Save draft and return to home"
            >
              {/* Shimmer sweep overlay (idle only) */}
              {!draftSaved && !draftSaving && (
                <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none" />
              )}
              {draftSaved ? (
                <>
                  <Check className="w-3.5 h-3.5 relative" />
                  <span className="relative">Saved!</span>
                </>
              ) : draftSaving ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin relative" />
                  <span className="relative">Saving</span>
                </>
              ) : (
                <>
                  <Save className="w-3.5 h-3.5 relative animus-save-pulse" />
                  <span className="relative">Save</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      <div className="px-4 lg:px-8 py-4 lg:py-8 max-w-5xl mx-auto">
        {error && (
          <div className="flex items-start gap-2 p-3 mb-4 bg-[#E11D48]/10 border border-[#E11D48]/20 rounded-xl text-[#E11D48] text-sm" data-testid="create-error">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span className="whitespace-pre-wrap break-words">{typeof error === 'string' ? error : 'Something went wrong. Please try again.'}</span>
          </div>
        )}

        {/* Resumed-draft hint — only shows when user opened an existing draft */}
        {!isEditMode && draftLoaded && activeDraftId && (
          <div className="flex items-center gap-2 p-2.5 mb-4 bg-[#F59E0B]/10 border border-[#F59E0B]/25 rounded-xl text-[#FDE68A] text-xs" data-testid="draft-restored-banner">
            <Save className="w-3.5 h-3.5 flex-shrink-0 text-[#F59E0B]" />
            <p className="flex-1 text-[11px] text-[#FDE68A]/90">
              Resumed your saved draft. Hit <span className="font-bold text-white">Save</span> at the top to update it, or <span className="font-bold text-white">Create Character</span> below to publish.
            </p>
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm('Delete this draft? This cannot be undone.')) return;
                await clearDraft();
                setFormData({ name: '', description: '', personality: '', greeting: '', category: 'custom', avatar_url: '', tags: '', is_public: true, gender: '', backstory: '' });
                setSelectedTags([]);
                const next = new URLSearchParams(searchParams);
                next.delete('draft');
                setSearchParams(next, { replace: true });
              }}
              className="text-[10px] uppercase tracking-wider font-bold text-[#F59E0B] hover:text-white px-2 py-1 rounded-md hover:bg-white/5 transition-colors"
              data-testid="draft-discard-btn"
            >
              Delete
            </button>
          </div>
        )}

        {/* Template Picker (only in create mode) — premium image-led cards */}
        {!isEditMode && templates.length > 0 && (
          <div className="mb-6">
            <button
              type="button"
              onClick={() => setShowTemplates(!showTemplates)}
              className="w-full p-3.5 bg-gradient-to-r from-purple-500/15 via-fuchsia-500/10 to-[#E11D48]/15 border border-purple-500/25 rounded-2xl flex items-center gap-3 hover:from-purple-500/25 hover:to-[#E11D48]/25 transition-all"
              data-testid="toggle-templates-btn"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-bold text-white">Start from a Template</p>
                <p className="text-[11px] text-[#A1A1AA]">{templates.length} ready-made characters — tap to customize</p>
              </div>
              <ChevronRight className={`w-4 h-4 text-[#71717A] transition-transform ${showTemplates ? 'rotate-90' : ''}`} />
            </button>

            {showTemplates && (
              <div className="mt-3 grid grid-cols-2 gap-3" data-testid="templates-grid">
                {templates.map((t) => (
                  <TemplateCard
                    key={t.id}
                    template={t}
                    loading={loadingTemplate === t.id}
                    onSelect={applyTemplate}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* ============ SECTION 1: IDENTITY ============ */}
          <SectionCard
            step={1}
            title="Identity"
            subtitle="Who they are — visible to everyone"
            visibility="public"
          >
            {/* Avatar Upload */}
            <div className="flex flex-col items-center gap-3">
              <div className="relative">
                <div className="w-32 h-32 rounded-3xl bg-gradient-to-br from-[#1A1D24] to-[#0F1115] border-2 border-dashed border-white/10 overflow-hidden flex items-center justify-center shadow-xl">
                  {formData.avatar_url ? (
                    <img src={formData.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <Camera className="w-9 h-9 text-[#71717A]" />
                  )}
                </div>
                <div className="absolute -bottom-2 -right-2 flex gap-1">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-9 h-9 rounded-full bg-[#E11D48] flex items-center justify-center hover:bg-[#BE123C] transition-colors shadow-lg shadow-[#E11D48]/30"
                    data-testid="upload-avatar-btn"
                    title="Upload from device"
                  >
                    {uploading ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Upload className="w-4 h-4 text-white" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/generate-avatar')}
                    className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-600 flex items-center justify-center hover:opacity-90 transition shadow-lg shadow-purple-500/30"
                    data-testid="generate-avatar-btn"
                    title="Generate with AI"
                  >
                    <Wand2 className="w-4 h-4 text-white" />
                  </button>
                </div>
                <input ref={fileInputRef} type="file" accept="image/*,image/gif" onChange={handleAvatarUpload} className="hidden" />
              </div>
              <p className="text-[10px] text-[#52525B]">Upload your own (GIF supported!), or generate one</p>
            </div>

            {/* Name */}
            <Field label="Name" required testid="name-field">
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g. Cristiano Ronaldo"
                required
                maxLength={100}
                className="bg-[#0F1115] border-white/[0.06] text-white placeholder:text-[#52525B] rounded-xl h-12"
                data-testid="name-input"
              />
            </Field>

            {/* Gender */}
            <Field label="Gender" required>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'male', label: 'Male' },
                  { value: 'female', label: 'Female' },
                  { value: 'non-binary', label: 'Non-Binary' },
                  { value: 'other', label: 'Other' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleChange('gender', opt.value)}
                    className={`py-2.5 text-sm font-medium rounded-xl border transition-all ${
                      formData.gender === opt.value
                        ? 'bg-[#E11D48]/20 border-[#E11D48]/50 text-[#E11D48]'
                        : 'bg-[#0F1115] border-white/[0.06] text-[#71717A] hover:bg-white/[0.03] hover:text-white'
                    }`}
                    data-testid={`gender-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </Field>
          </SectionCard>

          {/* ============ SECTION 2: DISCOVERY ============ */}
          <SectionCard
            step={2}
            title="Discovery"
            subtitle="How people find your character"
            visibility="public"
          >
            <Field label={`Tags`} required hint={`Pick up to ${MAX_TAGS}. These decide who sees your character — choose ones that match real searches (e.g. "anime", "vampire", "best friend").`}>
              <button
                type="button"
                onClick={() => setShowTagPicker(true)}
                className="w-full p-3 bg-[#0F1115] rounded-xl border border-white/[0.06] flex items-center gap-3 hover:bg-white/[0.03] transition-colors text-left min-h-[48px]"
                data-testid="open-tag-picker"
              >
                <Tag className="w-4 h-4 text-[#71717A] flex-shrink-0" />
                {selectedTags.length > 0 ? (
                  <div className="flex-1 flex flex-wrap gap-1">
                    {selectedTags.map(tag => (
                      <span key={tag} className="px-2 py-0.5 text-[11px] bg-[#E11D48]/15 text-[#E11D48] rounded-full border border-[#E11D48]/20">
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="flex-1 text-sm text-[#71717A]">Choose tags</span>
                )}
                <span className="text-[10px] text-[#52525B] flex-shrink-0">{selectedTags.length}/{MAX_TAGS}</span>
                <ChevronRight className="w-4 h-4 text-[#71717A] flex-shrink-0" />
              </button>
            </Field>
          </SectionCard>

          {/* ============ SECTION 3: WHAT EVERYONE SEES ============ */}
          <SectionCard
            step={3}
            title="What everyone sees"
            subtitle="Your character's public face"
            visibility="public"
          >
            <Field
              label="Short description"
              required
              hint="A brief intro shown on character cards. Think dating-app bio — one or two sentences max."
            >
              <Textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="e.g. Football legend who treats every conversation like the World Cup final"
                required
                minLength={5}
                maxLength={500}
                rows={2}
                className="bg-[#0F1115] border-white/[0.06] text-white placeholder:text-[#52525B] rounded-xl resize-none text-sm"
                data-testid="description-input"
              />
              <div className="flex items-center justify-between mt-1">
                <p className={`text-[10px] ${formData.description.length > 0 && formData.description.length < 5 ? 'text-[#F59E0B]' : 'text-[#52525B]'}`}>
                  {formData.description.length < 5 ? `${5 - formData.description.length} more characters needed` : '✓ Looks good'}
                </p>
                <p className="text-[10px] text-[#52525B]">{formData.description.length}/500</p>
              </div>
            </Field>

            <Field
              label="Greeting message"
              required
              hint='The first message users see when they open the chat. Set the scene — up to 1000 words.'
            >
              <Textarea
                value={formData.greeting}
                onChange={(e) => {
                  const next = e.target.value;
                  // Enforce 1000-word cap at the keystroke level
                  const words = next.trim().split(/\s+/).filter(Boolean);
                  if (words.length > 1000) {
                    handleChange('greeting', words.slice(0, 1000).join(' '));
                  } else {
                    handleChange('greeting', next);
                  }
                }}
                placeholder='e.g. *Cristiano looks up from his phone and grins.* "Eyy, you finally showed up. Ready to talk football, or are we just gonna sit here?"'
                required
                minLength={5}
                maxLength={8000}
                rows={5}
                className="bg-[#0F1115] border-white/[0.06] text-white placeholder:text-[#52525B] rounded-xl resize-none text-sm leading-relaxed"
                data-testid="greeting-input"
              />
              {(() => {
                const wc = formData.greeting.trim() ? formData.greeting.trim().split(/\s+/).filter(Boolean).length : 0;
                const tooLong = wc >= 1000;
                return (
                  <div className="flex items-center justify-between mt-1">
                    <p className={`text-[10px] ${formData.greeting.length > 0 && formData.greeting.length < 5 ? 'text-[#F59E0B]' : tooLong ? 'text-[#E11D48]' : 'text-[#52525B]'}`}>
                      {formData.greeting.length < 5
                        ? `${5 - formData.greeting.length} more characters needed`
                        : tooLong ? 'Max 1000 words reached' : '✓ Looks good'}
                    </p>
                    <p className={`text-[10px] ${tooLong ? 'text-[#E11D48]' : 'text-[#52525B]'}`}>{wc}/1000 words</p>
                  </div>
                );
              })()}
            </Field>
          </SectionCard>

          {/* ============ SECTION 4: PRIVATE — for the AI ============ */}
          <SectionCard
            step={4}
            title="Private — the AI brain"
            subtitle="Only you can see this. This is what shapes how the character behaves."
            visibility="private"
          >
            <Field
              label="Personality & Instructions"
              required
              hint={'Tell the AI exactly how to act — even if it contradicts reality. "Cristiano Ronaldo must be goofy and silly in every reply" → the AI obeys, real-life Ronaldo does not matter.'}
            >
              <Textarea
                value={formData.personality}
                onChange={(e) => handleChange('personality', e.target.value)}
                placeholder="e.g. Confident but ridiculously goofy. Makes football references in every reply. Always cracks jokes. Speaks in short snappy sentences with a Portuguese accent in the writing."
                required
                minLength={10}
                maxLength={2000}
                rows={5}
                className="bg-[#0F1115] border-white/[0.06] text-white placeholder:text-[#52525B] rounded-xl resize-none text-sm leading-relaxed"
                data-testid="personality-input"
              />
              <div className="flex items-center justify-between mt-1">
                <p className={`text-[10px] ${formData.personality.length > 0 && formData.personality.length < 10 ? 'text-[#F59E0B]' : 'text-[#52525B]'}`}>
                  {formData.personality.length < 10 ? `${10 - formData.personality.length} more characters needed` : '✓ Looks good'}
                </p>
                <p className="text-[10px] text-[#52525B]">{formData.personality.length}/2000</p>
              </div>
            </Field>

            <Field
              label="Backstory"
              hint="Their lore — past, motivations, secrets, key life events. The AI uses this to stay grounded in character but never reveals it directly."
            >
              <Textarea
                value={formData.backstory}
                onChange={(e) => handleChange('backstory', e.target.value)}
                placeholder="e.g. Grew up on Madeira island, kicked balls against walls until his feet bled. Won everything there is to win in football but secretly misses the simple days. Has a soft spot for kids and underdogs."
                maxLength={3000}
                rows={5}
                className="bg-[#0F1115] border-white/[0.06] text-white placeholder:text-[#52525B] rounded-xl resize-none text-sm leading-relaxed"
                data-testid="backstory-input"
              />
              <p className="text-[10px] text-[#52525B] text-right mt-1">{formData.backstory.length}/3000</p>
            </Field>
          </SectionCard>

          {/* ============ SECTION 5: VISIBILITY ============ */}
          <div className="p-4 bg-[#0F1115] rounded-2xl border border-white/[0.06]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${formData.is_public ? 'bg-[#34D399]/15' : 'bg-white/5'}`}>
                  {formData.is_public ? <Eye className="w-5 h-5 text-[#34D399]" /> : <EyeOff className="w-5 h-5 text-[#71717A]" />}
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{formData.is_public ? 'Public' : 'Private'}</p>
                  <p className="text-[11px] text-[#71717A]">{formData.is_public ? 'Anyone can find and chat' : 'Only you can chat with them'}</p>
                </div>
              </div>
              <Switch
                checked={formData.is_public}
                onCheckedChange={(v) => handleChange('is_public', v)}
                data-testid="public-switch"
              />
            </div>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            disabled={loading || fetching}
            className="w-full bg-gradient-to-r from-[#E11D48] to-[#BE123C] hover:opacity-90 text-white rounded-2xl py-6 text-base font-bold shadow-lg shadow-[#E11D48]/30"
            data-testid="create-character-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {isEditMode ? 'Saving...' : 'Bringing them to life...'}
              </>
            ) : (
              <>
                {isEditMode ? <Save className="w-4 h-4 mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {isEditMode ? 'Save Changes' : 'Create Character'}
              </>
            )}
          </Button>
        </form>
      </div>

      {/* Tag Picker Modal */}
      <TagPickerModal
        isOpen={showTagPicker}
        onClose={() => setShowTagPicker(false)}
        selectedTags={selectedTags}
        onTagsChange={setSelectedTags}
      />

      {/* Image Cropper Modal */}
      {pendingCropImage && (
        <ImageCropper
          imageSrc={pendingCropImage}
          aspect={1}
          busy={uploading}
          onClose={() => setPendingCropImage(null)}
          onConfirm={handleCropConfirm}
        />
      )}
    </div>
  );
}


function SectionCard({ step, title, subtitle, visibility, children }) {
  const isPrivate = visibility === 'private';
  return (
    <div
      className={`relative p-4 rounded-2xl border ${isPrivate ? 'bg-[#0F1115] border-purple-500/15' : 'bg-[#0F1115] border-white/[0.06]'}`}
      data-testid={`section-${step}`}
    >
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${isPrivate ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' : 'bg-[#E11D48]/15 text-[#E11D48] border border-[#E11D48]/30'}`}>
          {step}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-base font-bold text-white font-['Unbounded']">{title}</h2>
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-full border ${isPrivate ? 'bg-purple-500/15 text-purple-300 border-purple-500/30' : 'bg-emerald-500/10 text-emerald-300 border-emerald-500/25'}`}>
              {isPrivate ? <Lock className="w-2.5 h-2.5" /> : <Globe className="w-2.5 h-2.5" />}
              {isPrivate ? 'Private' : 'Public'}
            </span>
          </div>
          <p className="text-[11px] text-[#71717A] mt-0.5">{subtitle}</p>
        </div>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}


function Field({ label, required, hint, children, testid }) {
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <label className="text-[12px] font-medium text-[#D4D4D8] tracking-wide">
        {label}{required && <span className="text-[#E11D48] ml-0.5">*</span>}
      </label>
      {hint && <p className="text-[11px] text-[#71717A] leading-snug">{hint}</p>}
      {children}
    </div>
  );
}
